if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Auto-play videos on page load
        this.autoPlayActiveVideo();

        // Always listen for slide changes (for swipe/scroll navigation)
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      autoPlayActiveVideo() {
        // Find active media item or first media item
        const activeMedia = this.elements.viewer.querySelector('.is-active') || 
                           this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        const deferredMedia = activeMedia.querySelector('.deferred-media');
        if (deferredMedia) {
          // Auto-load video content on page load
          setTimeout(() => {
            deferredMedia.loadContent(false);
          }, 100);
        }
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        
        // Update thumbnail if thumbnails exist
        if (this.elements.thumbnails && activeMedia) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        
        // Auto-play video when sliding to a video slide
        if (activeMedia) {
          this.playVideoOnSlide(activeMedia);
        }
      }
      
      playVideoOnSlide(activeItem) {
        // Separate method for slide-triggered video playback
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        
        const mediaId = activeItem.dataset.mediaId;
        
        setTimeout(() => {
          // Pause other videos first
          this.elements.viewer.querySelectorAll('video').forEach((video) => {
            if (!activeItem.contains(video)) {
              video.pause();
            }
          });
          
          // On mobile, simulate clicking the thumbnail to trigger video playback
          if (!this.mql.matches && this.elements.thumbnails) {
            const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (thumbnail) {
              const thumbnailButton = thumbnail.querySelector('button');
              if (thumbnailButton) {
                thumbnailButton.click();
                return;
              }
            }
          }
          
          // Fallback / Desktop: load content directly
          if (deferredMedia.getAttribute('loaded')) {
            const video = deferredMedia.querySelector('video');
            if (video) {
              video.currentTime = 0;
              video.play().catch(() => {});
            }
          } else {
            deferredMedia.loadContent(false);
          }
        }, 50);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          // If video is already loaded, just play it
          if (deferredMedia.getAttribute('loaded')) {
            const video = deferredMedia.querySelector('video');
            if (video) {
              video.currentTime = 0;
              video.play();
            }
          } else {
            // Load content for first time
            deferredMedia.loadContent(false);
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
